import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import useApiEp from "../../hooks/useApiEp";
import {
  Box,
  List,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  Alert,
  Badge,
  ListItemButton,
  TextField,
  IconButton,
  Paper,
  styled,
  Stack,
  Button,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { MdCheck, MdDoneAll, MdErrorOutline } from "react-icons/md";
import { debounce } from "lodash";
import { API_ENDPOINT } from "../../utils/constants";
import {
  Send as SendIcon,
  Person as PersonIcon,
  ImportExport,
  DateRangeOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  getHeader,
  toastError,
  exportToExcel,
  getToken,
} from "../../utils/util";
import axios from "axios";
import { AppTheme } from "../../themes/AppTheme";
import SendMessage from "../../widgets/SendMessage";
import DateRangePicker from "../../widgets/DateRangePicker";
import { LoadingButton } from "@mui/lab";
import { useSearchParams } from "react-router-dom";
const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 320,
  overflowY: "auto",
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: "2px 0 8px rgba(0,0,0,0.05)",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));
const ChatContainer = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  backgroundColor: AppTheme.palette.background.default,
});
const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
}));
const ChatMessages = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.grey[400],
    borderRadius: "3px",
  },
}));
const MessageBubble = styled(Paper)(({ theme, isSent }) => ({
  padding: theme.spacing(1.5, 2),
  maxWidth: "70%",
  alignSelf: isSent ? "flex-end" : "flex-start",
  backgroundColor: isSent
    ? theme.palette.primary.main
    : theme.palette.background.paper,
  color: isSent
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  borderRadius: isSent ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
  boxShadow: isSent
    ? "0 2px 6px rgba(25, 118, 210, 0.3)"
    : "0 2px 6px rgba(0, 0, 0, 0.1)",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
}));
const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  boxShadow: "0 -2px 4px rgba(0,0,0,0.05)",
}));
const UnreadBadge = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: "white",
  borderRadius: "50%",
  width: 20,
  height: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.75rem",
}));
const Chats = () => {
  const [reloadChat, setReloadChat] = useState(false);
  const [searchParams] = useSearchParams();
  const paramValue = searchParams.get("tn");
  const [messageInput, setMessageInput] = useState("");
  const [tagType, setTagType] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchFrom, setSearchFrom] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [exportingChat, setExportingChat] = useState(false);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const messagesEndRef = useRef(null);
  const debouncedSetSearchFrom = useMemo(
    () => debounce((value) => setSearchFrom(value.trim()), 300),
    []
  );
  useEffect(() => {
    debouncedSetSearchFrom(searchInput);
    return () => debouncedSetSearchFrom.cancel();
  }, [searchInput, debouncedSetSearchFrom]);
  const {
    data: contacts = [],
    isLoading: isContactsLoading,
    isError: isContactsError,
    error: contactsError,
  } = useApiEp(
    `/api/get-my-chats?from=${searchFrom}&tn=${
      paramValue ? encodeURIComponent(paramValue) : ""
    }`
  );
  const {
    data: chatData,
    isLoading: isChatLoading,
    isError: isChatError,
    error: chatError,
  } = useApiEp(
    `/api/user-chats/${selectedContact?.from}?reload=${reloadChat}`,
    !!selectedContact
  );
  const handleContactClick = useCallback((contact) => {
    setSelectedContact(contact);
  }, []);
  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);
  const isMessageSent = (message) => {
    return message.from !== selectedContact?.from;
  };
  const getMessageContent = (message) => {
    if (message.type === "template") {
      const bodyComponent = message.components?.find(
        (component) => component.type === "BODY"
      );
      return bodyComponent?.text || message?.text || message?.body;
    } else if (message.type === "text" || message.type === "button") {
      return message.body || message?.text || message?.body;
    } else if (message.type === "interactive") {
      return message?.text || message?.body;
    }
    return message?.text || message?.body || "...";
  };
  const getStatusIcon = (status) => {
    const commonProps = { size: 14 };
    switch (status) {
      case "sent":
        return <MdCheck {...commonProps} style={{ color: "#919191" }} />;
      case "delivered":
        return <MdDoneAll {...commonProps} style={{ color: "#919191" }} />;
      case "read":
        return <MdDoneAll {...commonProps} style={{ color: "#34B7F1" }} />;
      case "failed":
        return <MdErrorOutline {...commonProps} style={{ color: "#E94437" }} />;
      default:
        return null;
    }
  };
  const getStatusText = (status) => {
    switch (status) {
      case "sent":
        return "Sent";
      case "delivered":
        return "Delivered";
      case "read":
        return "Read";
      case "failed":
        return "Failed";
      default:
        return "";
    }
  };
  const handleSendMessage = async (text, file) => {
    try {
      setSendingMessage(true); // Set to true when starting to send
      const formData = new FormData();
      if (text && text.trim()) {
        formData.append("message", text.trim());
      }
      formData.append("profileName", selectedContact?.profileName);
      formData.append("to", selectedContact?.from);
      if (file) {
        formData.append("file", file);
      }
      const endpoint = file
        ? `${API_ENDPOINT}/message/send-file-message`
        : `${API_ENDPOINT}/message/send-text-message`;
      const response = await fetch(endpoint, {
        method: "POST",
        body: file
          ? formData
          : JSON.stringify({
              message: text.trim(),
              profileName: selectedContact?.profileName,
              to: selectedContact?.from,
            }),
        headers: {
          ...(!file && { "Content-Type": "application/json" }),
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setReloadChat((prev) => !prev);
      toast.success("Message sent successfully");
      return result;
    } catch (error) {
      toastError(error);
      throw error;
    } finally {
      setSendingMessage(false);
      setMessageInput("");
    }
  };
  const formatIST = (iso) =>
    new Date(iso)
      .toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(/\//g, "-")
      .replace(", ", " ");
  const exportChat = async () => {
    const payload = {};
    if (tagType) {
      payload.tag = tagType.trim();
    }
    if (startDate && endDate) {
      payload.startDate = startDate.format("YYYY-MM-DD");
      payload.endDate = endDate.format("YYYY-MM-DD");
    }
    try {
      setExportingChat(true);
      const { data } = await axios.post(
        `${API_ENDPOINT}/message/export-chat?tn=${
          paramValue ? encodeURIComponent(paramValue) : ""
        }`,
        payload,
        getHeader()
      );
      const sheetData = data.map(
        ({ from, profileName, type, body, tag, updatedOn, campaignName }) => ({
          "From (Phone)": from,
          "Profile Name": profileName,
          "Message Type": type,
          "Message Body": body,
          Tag: tag,
          "Updated On (IST)": formatIST(updatedOn),
          "Campaign Name": campaignName,
        })
      );
      exportToExcel(sheetData, "chat_export.xlsx");
    } catch (error) {
      toastError(error);
    } finally {
      setExportingChat(false);
    }
  };
  // Scroll to bottom when chat is loaded or updated
  useEffect(() => {
    if (messagesEndRef.current && !isChatLoading && chatData?.messages) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatData?.messages, selectedContact, isChatLoading]);
  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 64px)",
        bgcolor: "background.default",
      }}
    >
      <SidebarContainer>
        <Box sx={{ p: 2, pb: 0 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name/phone"
            value={searchInput}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <IconButton edge="start" disabled>
                  <SearchIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
        </Box>
        {isContactsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : isContactsError ? (
          <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>
            Error: {contactsError?.message || "Failed to load contacts"}
          </Alert>
        ) : (
          <List sx={{ p: 1 }}>
            {contacts.map((contact) => (
              <ListItemButton
                key={contact.from}
                selected={selectedContact?.from === contact.from}
                onClick={() => handleContactClick(contact)}
                sx={{ py: 1.5 }}
              >
                <ListItemAvatar>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                      contact.unreadCount ? (
                        <UnreadBadge>{contact.unreadCount}</UnreadBadge>
                      ) : null
                    }
                  >
                    <Avatar>{contact?.profileName?.[0]?.toUpperCase()}</Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="body1" fontWeight={500}>
                      {contact.profileName}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {contact.from}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </SidebarContainer>
      <ChatContainer>
        {selectedContact ? (
          <>
            <ChatHeader>
              <Avatar sx={{ bgcolor: "primary.main" }}>
                {selectedContact?.profileName?.charAt(0).toUpperCase()}
              </Avatar>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width={"100%"}
                spacing={2}
              >
                <Box>
                  <Typography
                    variant="h6"
                    noWrap
                    sx={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedContact.profileName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedContact.from}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <DateRangePicker
                    startLabel="From"
                    endLabel="To"
                    onChange={(newRange) => {
                      setStartDate(newRange.startDate); // Keep as dayjs
                      setEndDate(newRange.endDate); // Keep as dayjs
                    }}
                    format="DD/MM/YYYY"
                    showQuickSelect={true}
                  />
                  <TextField
                    value={tagType}
                    onChange={(e) => setTagType(e.target.value)}
                    variant="outlined"
                    label="Enter Tag"
                  />
                  <LoadingButton
                    sx={{ width: 256 }}
                    loading={exportingChat}
                    onClick={exportChat}
                    startIcon={<ImportExport />}
                    variant="outlined"
                  >
                    Export Chat
                  </LoadingButton>
                </Stack>
              </Stack>
            </ChatHeader>
            <ChatMessages>
              {isChatLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress size={32} />
                </Box>
              )}
              {isChatError && (
                <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>
                  Error: {chatError?.message || "Failed to load messages"}
                </Alert>
              )}
              {chatData?.messages?.map((message) => {
                const isSent = isMessageSent(message);
                const formattedTime = formatIST(message.updatedOn).slice(0, -3);
                return (
                  <MessageBubble key={message.id} isSent={isSent}>
                    <Typography variant="body1">
                      {getMessageContent(message)}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color={isSent ? "inherit" : "text.secondary"}
                        sx={{ opacity: 0.7 }}
                      >
                        {formattedTime}
                      </Typography>
                      {isSent && message.status && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                          }}
                        >
                          {getStatusIcon(message.status)}
                          <Typography
                            variant="caption"
                            color="inherit"
                            sx={{ fontSize: "0.65rem", opacity: 0.7 }}
                          >
                            {getStatusText(message.status)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </MessageBubble>
                );
              })}
              {!isChatLoading &&
                !isChatError &&
                (!chatData?.messages || chatData.messages.length === 0) && (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: 2,
                      color: "text.secondary",
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                    <Typography variant="body1">
                      No messages yet. Start the conversation!
                    </Typography>
                  </Box>
                )}
              <div ref={messagesEndRef} />
            </ChatMessages>
            <InputContainer>
              <SendMessage onSend={handleSendMessage} />
            </InputContainer>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
              color: "text.secondary",
            }}
          >
            <PersonIcon sx={{ fontSize: 64, opacity: 0.3 }} />
            <Typography variant="h6" fontWeight={500}>
              Select a contact to start chatting
            </Typography>
            <Typography
              variant="body2"
              sx={{ maxWidth: 400, textAlign: "center" }}
            >
              Choose a contact from the list to view your conversation or start
              a new one.
            </Typography>
          </Box>
        )}
      </ChatContainer>
      {selectedContact && <UserProfile selectedContact={selectedContact} />}
    </Box>
  );
};
export default Chats;
const UserProfile = ({ selectedContact }) => {
  const [tag, setTag] = useState("");
  const [tagging, setTagging] = useState(false);
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/message/get-tagged-user?from=${selectedContact.from}`
  );
  useEffect(() => {
    if (data && data?.tag) {
      setTag(data.tag);
    }
  }, [data]);
  return (
    <Box bgcolor={"white"} width={300} borderLeft={"1px solid #ddd"}>
      <Box mt={4} justifyContent={"center"} display={"flex"}>
        <Avatar sx={{ bgcolor: "primary.main", height: 48, width: 48 }}>
          {selectedContact?.profileName?.charAt(0)?.toUpperCase()}
        </Avatar>
      </Box>
      <Box p={2} justifyContent={"center"}>
        <Typography
          variant="h6"
          noWrap
          textAlign={"center"}
          sx={{
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectedContact.profileName}
        </Typography>
        <div style={{ textAlign: "center", fontSize: "12px" }}>
          {selectedContact.from}
        </div>
      </Box>
      <Divider sx={{ mx: 1 }} />
      <Box m={2}>
        <TextField
          fullWidth
          variant="outlined"
          label="Enter Tag"
          size="small"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          margin="normal"
        />
        <LoadingButton
          onClick={async () => {
            const payload = { phoneNumber: selectedContact.from };
            if (tag) {
              payload.tag = tag.trim();
            }
            try {
              setTagging(true);
              const response = await axios.post(
                `${API_ENDPOINT}/message/tag-user`,
                payload,
                getHeader()
              );
              toast.success(response.data.message);
              refetch();
            } catch (error) {
              toastError(error);
            } finally {
              setTagging(false);
            }
          }}
          loading={tagging}
          fullWidth
          variant="outlined"
        >
          Tag Contact
        </LoadingButton>
        <Typography mt={2} variant="body1">
          *Please ensure you are in 24 hour session window with recipient to
          send messages
        </Typography>
      </Box>
    </Box>
  );
};
